export type SentryEventLike = {
    exception?: {
        values?: Array<{
            value?: string;
            stacktrace?: {
                frames?: Array<{
                    filename?: string | null;
                    abs_path?: string | null;
                }>;
            };
        }>;
    };
};

export type SentryTransactionEventLike = {
    transaction?: string;
};

export type SentrySamplingContextLike = {
    name?: string;
    transactionContext?: {
        name?: string;
    };
};

/** Applied when the env rate is missing, invalid, or the legacy 100% default. */
export const DEFAULT_TRACES_SAMPLE_RATE = 0.1;

export const SENTRY_IGNORED_TRANSACTION_NAMES = [
    "/readiness_check",
    "/up",
    "/robots.txt",
];

export const THIRD_PARTY_SCRIPT_PATTERNS = [
    /googletagmanager\.com/i,
    /\/gtm\.js/i,
];

export const FIRST_PARTY_SCRIPT_PATTERNS = [
    /\/resources\/js\//,
    /\/build\/assets\//,
    /\/@vite\//,
    /\/@fs\//,
];

function getFrameSource(frame: {
    filename?: string | null;
    abs_path?: string | null;
}): string {
    return `${frame.filename ?? ""} ${frame.abs_path ?? ""}`;
}

/**
 * Ignore errors that originate exclusively from third-party scripts such as GTM.
 * GTM custom tags often query DOM nodes that do not exist on every page, which
 * surfaces as noisy TypeErrors in Sentry without affecting our application code.
 */
export function isThirdPartyOnlyScriptError(event: SentryEventLike): boolean {
    const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
    if (frames.length === 0) {
        return false;
    }

    const hasThirdPartyFrame = frames.some((frame) =>
        THIRD_PARTY_SCRIPT_PATTERNS.some((pattern) =>
            pattern.test(getFrameSource(frame)),
        ),
    );
    if (!hasThirdPartyFrame) {
        return false;
    }

    const hasFirstPartyFrame = frames.some((frame) =>
        FIRST_PARTY_SCRIPT_PATTERNS.some((pattern) =>
            pattern.test(getFrameSource(frame)),
        ),
    );

    return !hasFirstPartyFrame;
}

export function beforeSendSentryEvent<T extends SentryEventLike>(
    event: T,
): T | null {
    return isThirdPartyOnlyScriptError(event) ? null : event;
}

/**
 * Interpret a traces sample rate. A value of 1 (100%) is the historical
 * project default and is remapped so a leftover production env cannot
 * exhaust the Sentry performance quota after deploy.
 */
export function resolveTracesSampleRate(
    rawTracesSampleRate: string | number | undefined,
): number {
    if (rawTracesSampleRate === undefined || rawTracesSampleRate === "") {
        return DEFAULT_TRACES_SAMPLE_RATE;
    }

    const rate =
        typeof rawTracesSampleRate === "number"
            ? rawTracesSampleRate
            : Number.parseFloat(rawTracesSampleRate);

    if (!Number.isFinite(rate) || rate < 0) {
        return DEFAULT_TRACES_SAMPLE_RATE;
    }

    if (rate >= 1) {
        return DEFAULT_TRACES_SAMPLE_RATE;
    }

    return rate;
}

export function isIgnoredSentryTransaction(name: string | undefined): boolean {
    if (!name) {
        return false;
    }

    const normalized = name.toLowerCase();

    return SENTRY_IGNORED_TRANSACTION_NAMES.some((ignored) =>
        transactionNameMatchesNeedle(normalized, ignored.toLowerCase()),
    );
}

function transactionNameMatchesNeedle(name: string, needle: string): boolean {
    if (!needle.startsWith("/")) {
        return name.includes(needle);
    }

    let fromIndex = 0;
    while (fromIndex <= name.length) {
        const pos = name.indexOf(needle, fromIndex);
        if (pos === -1) {
            return false;
        }

        const after = name[pos + needle.length] ?? "";
        if (after === "" || after === "?" || after === " " || after === "/") {
            return true;
        }

        fromIndex = pos + 1;
    }

    return false;
}

export function createSentryTracesSampler(sampleRate: number) {
    return (samplingContext: SentrySamplingContextLike): number => {
        const name =
            samplingContext.name ??
            samplingContext.transactionContext?.name ??
            "";

        if (isIgnoredSentryTransaction(name)) {
            return 0;
        }

        return sampleRate;
    };
}

export function beforeSendSentryTransaction<
    T extends SentryTransactionEventLike,
>(event: T): T | null {
    return isIgnoredSentryTransaction(event.transaction) ? null : event;
}

export function sentryPerformanceTracingOptions(
    rawTracesSampleRate: string | number | undefined,
): {
    tracesSampleRate: number;
    tracesSampler: (context: SentrySamplingContextLike) => number;
    ignoreTransactions: string[];
    beforeSendTransaction: typeof beforeSendSentryTransaction;
} {
    const tracesSampleRate = resolveTracesSampleRate(rawTracesSampleRate);

    return {
        tracesSampleRate,
        tracesSampler: createSentryTracesSampler(tracesSampleRate),
        ignoreTransactions: [...SENTRY_IGNORED_TRANSACTION_NAMES],
        beforeSendTransaction: beforeSendSentryTransaction,
    };
}

export const sentryDenyUrls: Array<string | RegExp> = [
    /googletagmanager\.com/i,
    /gtm\.js/i,
];
